import { AlignmentType, Document, HeadingLevel, Packer, Paragraph, TextRun } from 'docx';
import jsPDF from 'jspdf';
import { ChevronDown, ChevronUp, Copy, Loader2, X } from 'lucide-react';
import { marked } from 'marked';
import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { FaRegFilePdf, FaRegFileWord } from 'react-icons/fa6';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button.js';
import { Input } from '../../../components/ui/input.js';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../../../components/ui/sheet.js';
import { cn } from '../../../lib/utils.js';

/**
 * AI-generated technical-summary sheet — opens from the sparkle icon
 * in the scenarios table (developer mode only). Mirrors the legacy
 * `wf-audit/src/slices/scenario/components/scenarioThumb/Summary.tsx`
 * `type="technical"` flow:
 *
 *   • Markdown content rendered via `marked` (GFM + breaks)
 *   • Live find-in-page: highlights matches, prev/next/clear controls
 *   • Footer: Copy plain text, export to Word (.docx), export to PDF
 *
 * Word/PDF export is **client-side** (legacy posts to a Next.js
 * `/api/scenario/generate-docx` route that uses `docx` + `puppeteer`;
 * we use the `docx` package + `jsPDF` directly in the browser). This
 * keeps the AI-summary export path off the Lambda critical path —
 * the engine's `/v1/scenarios/{id}/document` endpoint is for the full
 * structured scenario document, not the AI summary text.
 */
export interface ScenarioAiSummarySheetProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  title: string;
  text: string;
}

export function ScenarioAiSummarySheet({
  open,
  onOpenChange,
  title,
  text,
}: ScenarioAiSummarySheetProps): JSX.Element {
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState<number[]>([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [highlightedHtml, setHighlightedHtml] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const [isDownloadingWord, setIsDownloadingWord] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const textContentRef = useRef<HTMLDivElement>(null);

  // Convert markdown to HTML only when sheet is open. `marked.parse`
  // is synchronous when `async: false` (the default). We typecast
  // because the .d.ts overloads make this look like it could return
  // a Promise — at runtime, it doesn't.
  const htmlContent = useMemo<string>(() => {
    if (!text || !open) return '';
    marked.setOptions({ gfm: true, breaks: true });
    return marked.parse(text) as string;
  }, [text, open]);

  const scrollToHighlightedMatch = useCallback(() => {
    const match = textContentRef.current?.querySelector('.highlight-current');
    if (match) {
      match.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  const updateMatches = useCallback(() => {
    if (!htmlContent || !query) {
      setHighlightedHtml(htmlContent);
      setMatches([]);
      return;
    }
    const foundMatches: number[] = [];
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Negative lookbehind keeps us outside HTML tag attributes; the
    // negative lookahead keeps the match outside a tag entirely.
    const regex = new RegExp(`(?<!<[^>]*)(${escapedQuery})(?![^<]*>)`, 'gi');
    const newText = htmlContent.replace(regex, (match, _g1, idx: number) => {
      foundMatches.push(idx);
      const isCurrent = foundMatches.length === currentMatchIndex + 1;
      return `<mark class="${isCurrent ? 'highlight-current' : 'highlight'}">${match}</mark>`;
    });
    setMatches(foundMatches);
    setHighlightedHtml(newText);
  }, [currentMatchIndex, query, htmlContent]);

  useEffect(() => {
    setMatches([]);
    setCurrentMatchIndex(0);
    if (!query) {
      setHighlightedHtml(htmlContent);
      return;
    }
    updateMatches();
    // We intentionally re-run updateMatches when query or htmlContent
    // changes; currentMatchIndex changes trigger a separate effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, htmlContent]);

  useEffect(() => {
    if (query) updateMatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMatchIndex]);

  useEffect(() => {
    scrollToHighlightedMatch();
  }, [highlightedHtml, scrollToHighlightedMatch]);

  const nextMatch = (): void => {
    if (matches.length > 0) {
      setCurrentMatchIndex((i) => (i + 1) % matches.length);
    }
  };
  const prevMatch = (): void => {
    if (matches.length > 0) {
      setCurrentMatchIndex((i) => (i - 1 + matches.length) % matches.length);
    }
  };
  const clearSearch = (): void => {
    setQuery('');
    setMatches([]);
    setCurrentMatchIndex(0);
  };

  const safeFilename = useMemo(
    () =>
      title
        .replace(/[^a-z0-9]/gi, '_')
        .toLowerCase()
        .slice(0, 80) || 'ai_summary',
    [title],
  );

  async function handleCopy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      toast.error(`Copy failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  /**
   * Build a `.docx` directly in the browser via the `docx` library.
   * The legacy route did the same on the server with the same
   * package — same output shape (one Paragraph per line, headings
   * promoted by leading `#` count). We keep the rules simple because
   * Markdown→DOCX with full structural fidelity is a separate
   * project; users wanting pixel-perfect rendering should use the
   * PDF export which goes through the actual HTML view.
   */
  async function handleDownloadWord(): Promise<void> {
    if (isDownloadingWord || isDownloadingPdf) return;
    setIsDownloadingWord(true);
    try {
      const lines = text.split('\n');
      const headingLevels = [
        HeadingLevel.HEADING_1,
        HeadingLevel.HEADING_2,
        HeadingLevel.HEADING_3,
        HeadingLevel.HEADING_4,
        HeadingLevel.HEADING_5,
        HeadingLevel.HEADING_6,
      ] as const;
      const children: Paragraph[] = lines.map((line) => {
        const trimmed = line.trimStart();
        const hashMatch = /^(#{1,6})\s+(.*)$/.exec(trimmed);
        const hashPrefix = hashMatch?.[1];
        const hashText = hashMatch?.[2];
        if (hashMatch && hashPrefix && hashText !== undefined) {
          const depth = hashPrefix.length;
          const heading = headingLevels[depth - 1] ?? HeadingLevel.HEADING_6;
          return new Paragraph({
            heading,
            children: [new TextRun({ text: hashText })],
          });
        }
        return new Paragraph({
          children: [new TextRun({ text: line })],
          alignment: AlignmentType.LEFT,
        });
      });
      const doc = new Document({
        sections: [{ properties: {}, children }],
      });
      const blob = await Packer.toBlob(doc);
      triggerDownload(blob, `${safeFilename}.docx`);
    } catch (err) {
      toast.error(`Word export failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsDownloadingWord(false);
    }
  }

  /**
   * Build a `.pdf` directly in the browser via jsPDF. Wraps long
   * lines automatically and increments the page when we run off the
   * bottom. No styling beyond plain text — the AI summary is
   * read-mostly anyway, and the user has the Word option for
   * structured edits.
   */
  async function handleDownloadPdf(): Promise<void> {
    if (isDownloadingWord || isDownloadingPdf) return;
    setIsDownloadingPdf(true);
    try {
      const doc = new jsPDF({ unit: 'pt', format: 'letter' });
      const marginX = 48;
      const marginY = 48;
      const pageHeight = doc.internal.pageSize.getHeight();
      const pageWidth = doc.internal.pageSize.getWidth();
      const wrapWidth = pageWidth - marginX * 2;
      const lineHeight = 14;
      let cursorY = marginY;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      // Title
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(title, marginX, cursorY);
      cursorY += 24;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      // Body — split paragraphs on \n\n so we can put a blank line
      // between them. Within a paragraph, jsPDF's `splitTextToSize`
      // handles the word-wrap.
      const paragraphs = text.split(/\n{2,}/);
      for (const para of paragraphs) {
        const lines = doc.splitTextToSize(para, wrapWidth);
        for (const line of lines) {
          if (cursorY + lineHeight > pageHeight - marginY) {
            doc.addPage();
            cursorY = marginY;
          }
          doc.text(line, marginX, cursorY);
          cursorY += lineHeight;
        }
        cursorY += lineHeight / 2;
      }
      const blob = doc.output('blob');
      triggerDownload(blob, `${safeFilename}.pdf`);
    } catch (err) {
      toast.error(`PDF export failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsDownloadingPdf(false);
    }
  }

  const renderedHtml = highlightedHtml || htmlContent;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col bg-[color:var(--surface-overlay)] text-foreground sm:max-w-[640px]"
      >
        <SheetHeader>
          <SheetTitle className="text-foreground">{title}</SheetTitle>
        </SheetHeader>

        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Input
              type="text"
              placeholder="Search…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && nextMatch()}
              className="bg-foreground/5 text-foreground placeholder:text-foreground/40"
            />
            {query.length > 0 ? (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-foreground/50">
                {matches.length > 0 ? currentMatchIndex + 1 : 0}/{matches.length}
              </span>
            ) : null}
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={nextMatch}
            disabled={matches.length === 0}
            title="Next match"
            className="border-foreground/15 text-foreground/70 hover:border-foreground/40 hover:text-foreground"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={prevMatch}
            disabled={matches.length === 0}
            title="Previous match"
            className="border-foreground/15 text-foreground/70 hover:border-foreground/40 hover:text-foreground"
          >
            <ChevronUp className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={clearSearch}
            disabled={query.length === 0}
            title="Clear search"
            className="border-foreground/15 text-foreground/70 hover:border-foreground/40 hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div ref={textContentRef} className="ai-summary-body block flex-1 overflow-auto pr-2">
          <article
            className="whitespace-pre-wrap text-sm text-foreground/85"
            // marked-rendered, then prose-styled inline so we don't
            // need to ship a tailwind-typography dep just for this
            // one surface. Mirrors the legacy class-injection trick.
            dangerouslySetInnerHTML={{
              __html: renderedHtml
                .replace(/<h1>/g, '<h1 class="mt-6 mb-3 text-2xl font-bold text-foreground">')
                .replace(/<h2>/g, '<h2 class="mt-5 mb-2 text-xl font-bold text-foreground">')
                .replace(/<h3>/g, '<h3 class="mt-4 mb-2 text-lg font-semibold text-foreground">')
                .replace(/<h4>/g, '<h4 class="mt-3 mb-2 text-base font-medium text-foreground/90">')
                .replace(/<h5>/g, '<h5 class="mt-2 mb-1 text-sm font-medium text-foreground/90">')
                .replace(/<h6>/g, '<h6 class="mt-2 mb-1 text-xs font-medium text-foreground/80">')
                .replace(/<ul>/g, '<ul class="my-2 ml-5 list-disc">')
                .replace(/<ol>/g, '<ol class="my-2 ml-5 list-decimal">')
                .replace(
                  /<table>/g,
                  '<table class="my-4 w-full table-auto border-collapse border border-foreground/15 text-left">',
                )
                .replace(
                  /<th>/g,
                  '<th class="border border-foreground/15 bg-foreground/5 px-3 py-1.5 text-xs font-semibold">',
                )
                .replace(/<td>/g, '<td class="border border-foreground/15 px-3 py-1.5 text-xs">'),
            }}
          />
        </div>

        <SheetFooter className="border-t border-foreground/10 pt-3">
          <div className="flex w-full items-center justify-between">
            <Button
              variant="outline"
              onClick={handleCopy}
              className="border-foreground/15 text-foreground/70 hover:border-foreground/40 hover:text-foreground"
            >
              <Copy
                className={cn(
                  'mr-1.5 h-4 w-4',
                  isCopied && 'text-emerald-700 dark:text-emerald-300',
                )}
              />
              {isCopied ? 'Copied!' : 'Copy'}
            </Button>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleDownloadWord}
                disabled={isDownloadingWord || isDownloadingPdf}
                className="border-foreground/15 text-foreground/70 hover:border-foreground/40 hover:text-foreground"
              >
                {isDownloadingWord ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <FaRegFileWord className="mr-1.5 h-4 w-4" />
                )}
                {isDownloadingWord ? 'Generating…' : 'Word'}
              </Button>
              <Button
                variant="outline"
                onClick={handleDownloadPdf}
                disabled={isDownloadingWord || isDownloadingPdf}
                className="border-foreground/15 text-foreground/70 hover:border-foreground/40 hover:text-foreground"
              >
                {isDownloadingPdf ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <FaRegFilePdf className="mr-1.5 h-4 w-4" />
                )}
                {isDownloadingPdf ? 'Converting…' : 'PDF'}
              </Button>
            </div>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    URL.revokeObjectURL(url);
  }
}
